// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// Primordia — Target Architecture
//
// LeanIX ships a "Target Architecture" diagram type. For Optimum it is the
// deliverable that matters most: a client's Epic future state on AWS, drawn
// from the Epic Cloud Specifications Guide instead of hand-drawn in Visio for
// every engagement.
//
// This screen RENDERS what the CSG target-state screen CAPTURES. It reads the
// imported CSG versions out of localStorage ('primordia.csg.<tenantId>', the
// same records CsgTargetState writes) strictly read-only — it never imports a
// workbook, never edits one, and never writes that key. Nothing here creates
// fact sheets either: the CSG is a supplier document, not a discovered fact
// about the estate.
//
// THE SHAPE OF THE DRAWING IS EPIC'S, NOT OURS.
//   columns = Stamp        (Production, Alternate Production, Build, Training,
//                           Isolated Recovery)
//   rows    = Epic Tier    (Operational Database, Relational Database,
//                           Presentation, Web & Service)
// Each cell holds the cloud resources Epic specifies for that tier in that
// stamp: instance type, quantity, OS disk, and the volumes from the Storage
// Bill of Materials. That grid IS how Epic expresses the architecture, so it is
// preserved rather than re-drawn into something prettier that a client would
// then have to translate back.
//
// Storage is drawn ON the resource it belongs to, never in a separate box.
// io1/io2 with provisioned IOPS versus gp3 is the single most cost- and
// performance-significant distinction in an Epic build, so the disk type drives
// the accent bar, the swatch and the legend counts.
//
// Four things this screen refuses to do:
//   1. Infer sizing. The workbook states instance types and quantities; it does
//      not state vCPU or RAM per type. Those never appear here, in any form,
//      not even "approximately". Any figure the workbook does not supply prints
//      as "—" with the reason attached.
//   2. Silently attach a volume. A Storage Bill of Materials line is joined to a
//      compute resource by matching its volume description to the Epic Resource
//      name (constrained by tier/stamp when the storage sheet states them). That
//      is an INFERENCE, it is labelled as one, and anything ambiguous or
//      unmatched is listed unattached rather than quietly folded into a total.
//   3. Present target and current state as a mapping. The current-state panel is
//      context only, off by default, and says so — a CSG may describe a
//      greenfield build with no current-state equivalent at all.
//   4. Pretend a CSG is final. It is a point-in-time draft that gets reissued,
//      which is why version comparison is on the same page as the drawing.
//
// Rendering follows DataFlowDiagram: one SVG routine drives the screen, the PNG
// and the PDF, so what a consultant sees is exactly what the client receives.
// Exports reuse exports.js — the PDF goes through printElementToPdf, which
// copies innerHTML, so the drawing must be SVG; a <canvas> would arrive blank.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, CircularProgress, Divider, MenuItem, Paper,
  Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { computeScope, csgTotals, parseNum, readDb } from './CsgTargetState';
import { exportCanvasPng, exportCsv, printElementToPdf } from './exports';
import PrimordiaLogo from './PrimordiaLogo';
import store from './store';

// ---- Optimum palette --------------------------------------------------------
const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const AMBER = '#B26A00';
const GREEN = '#2E7D32';
const HEAD = 'sectionHeader.main';

const FONT = "-apple-system, 'Segoe UI', Roboto, Arial, sans-serif";
const PRINT_ID = 'target-architecture-print';
const DASH = '—';
const NONE = '__none__';

// Printable area: Letter/A4 landscape at 10 mm margins, the smaller of the two,
// so one PDF prints uncropped on either.
const PAGE_W = 970;
const PAGE_H = 715;
const MAP_H = PAGE_H - 26;

// ---------------------------------------------------------------------------
// Geometry. A stamp is a column, a tier is a row, and both are fixed-width /
// measured-height so the grid never overlaps itself no matter how lopsided the
// CSG is (a production CSG runs 66 compute lines; an Isolated Recovery one, 8).
// ---------------------------------------------------------------------------
const COL_W = 292;
const COL_GAP = 14;
const ROW_HEAD_W = 136;
const STAMP_HEAD_H = 46;
const ROW_GAP = 8;
const CELL_PAD = 9;
const CARD_GAP = 7;
const CARD_W = COL_W - CELL_PAD * 2;
const EMPTY_CELL_H = 34;
const TOTALS_H = 86;
const OVERALL_H = 62;

// ---------------------------------------------------------------------------
// The Epic vocabulary, in Epic's own order. Anything the workbook says that is
// not on these lists keeps its verbatim label and is appended — never folded
// into an "Other" bucket, which would hide a stamp somebody has to build.
// ---------------------------------------------------------------------------
const STAMP_ORDER = ['production', 'alternateproduction', 'build', 'training', 'isolatedrecovery'];
const TIER_ORDER = ['operationaldatabase', 'relationaldatabase', 'presentation', 'webservice'];

// A legend entry sits in a 320 px column at 10 px: about 58 characters once the
// swatch is allowed for. Longer wording is truncated there and spelled out in
// full in the notes underneath, rather than printing over its neighbour.
const LEGEND_CHARS = 58;

const NOT_STATED_STAMP = 'Stamp not stated';
const NOT_STATED_TIER = 'Tier not stated';

// ---- Small helpers ----------------------------------------------------------
const clean = (v) => String(v === undefined || v === null ? '' : v).replace(/\s+/g, ' ').trim();
const norm = (v) => clean(v).toLowerCase().replace(/[^a-z0-9]/g, '');
const fmt = (n) => (n === null || n === undefined || Number.isNaN(n) ? DASH : Number(n).toLocaleString());
const nowStamp = () => new Date().toLocaleString();
const fileStamp = () => new Date().toISOString().slice(0, 10);
const trunc = (s, n) => (String(s).length > n ? `${String(s).slice(0, n - 1)}…` : String(s));

function svgEsc(s) {
  return String(s === undefined || s === null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function wrapLabel(name, perLine, maxLines) {
  const words = String(name || '').split(/\s+/).filter(Boolean);
  const packed = [];
  let cur = '';
  words.forEach((w) => {
    if (!cur) { cur = w; return; }
    if ((`${cur} ${w}`).length <= perLine) cur = `${cur} ${w}`;
    else { packed.push(cur); cur = w; }
  });
  if (cur) packed.push(cur);

  const lines = [];
  packed.forEach((l) => {
    let s = l;
    while (s.length > perLine) { lines.push(s.slice(0, perLine)); s = s.slice(perLine); }
    if (s) lines.push(s);
  });
  if (!lines.length) return [''];
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = `${kept[maxLines - 1].slice(0, Math.max(1, perLine - 1))}…`;
    return kept;
  }
  return lines;
}

// Order helper: known vocabulary first in Epic's order, then anything else
// alphabetically, then the "not stated" bucket last.
function orderBy(list, canonical, notStated) {
  return [...list].sort((a, b) => {
    if (a === notStated) return 1;
    if (b === notStated) return -1;
    const ai = canonical.indexOf(norm(a));
    const bi = canonical.indexOf(norm(b));
    return (ai === -1 ? 90 : ai) - (bi === -1 ? 90 : bi) || a.localeCompare(b);
  });
}

// ---------------------------------------------------------------------------
// Disk types. The accent that carries the most money and the most risk.
// io1 / io2 buy provisioned IOPS; gp3 does not. In an Epic build that single
// distinction moves the run-rate and the ODB latency budget more than anything
// else in the workbook, so it is the loudest mark on the page.
// ---------------------------------------------------------------------------
const DISK_STYLES = [
  {
    key: 'io', match: /^io[12]\b|^io[12]$/, rank: 4,
    label: 'io1 / io2 — provisioned IOPS',
    color: '#C62828', fill: '#fdeeee', provisioned: true,
    note: 'IOPS are bought and paid for per volume — the ODB latency and cost lever',
  },
  {
    key: 'gp', match: /^gp[23]\b|^gp[23]$/, rank: 3,
    label: 'gp2 / gp3 — general purpose SSD',
    color: BLUE, fill: '#e8f0fe',
    note: 'baseline SSD, no separately provisioned IOPS unless the workbook states them',
  },
  {
    key: 'hdd', match: /^(st1|sc1)\b|^(st1|sc1)$/, rank: 2,
    label: 'st1 / sc1 — throughput / cold HDD',
    color: '#6A1B9A', fill: '#f3e5f5',
    note: 'sequential throughput tiers — not for database volumes',
  },
];
const DISK_UNKNOWN = {
  key: '__none__', rank: 0, label: 'disk type not stated', color: '#B0BEC5', fill: '#f4f6f8',
  dashed: true, note: 'the workbook cell was empty — nothing has been assumed',
};
const DISK_OTHER = {
  key: '__other__', rank: 1, label: 'other disk type (verbatim)', color: '#78909C', fill: '#eceff1',
  dashed: true, note: 'a value this diagram does not style — shown exactly as the workbook wrote it',
};

function diskStyle(raw) {
  const v = clean(raw);
  if (!v) return DISK_UNKNOWN;
  const n = v.toLowerCase();
  const hit = DISK_STYLES.find((d) => d.match.test(n));
  return hit || { ...DISK_OTHER, label: `${v} — ${DISK_OTHER.label}` };
}

// ---- Version-comparison marks ----------------------------------------------
const CHANGE_STYLES = {
  Added: { color: GREEN, fill: '#eaf5ec', badge: 'ADDED' },
  Removed: { color: AMBER, fill: '#fff8e6', badge: 'REMOVED' },
  Resized: { color: BLUE, fill: '#e8f0fe', badge: 'QTY' },
  Retyped: { color: '#6A1B9A', fill: '#f3e5f5', badge: 'TYPE' },
};

// ---------------------------------------------------------------------------
// Model — from stored CSG line items to cards
// ---------------------------------------------------------------------------
// A card is one folded group of Compute Bill of Materials lines sharing stamp +
// tier + Epic Resource + Cloud Resource. Quantities add; nothing else is
// merged, and a quantity that was never stated stays null rather than becoming
// a zero that would silently drag a total down.
function foldCards(csg) {
  const map = new Map();
  ((csg && csg.lines) || []).forEach((l) => {
    const stamp = clean(l.stamp) || NOT_STATED_STAMP;
    const tier = clean(l.epicTier) || NOT_STATED_TIER;
    const resource = clean(l.epicResource);
    const cloud = clean(l.cloudResource);
    const key = `${norm(stamp)}|${norm(tier)}|${norm(resource)}|${norm(cloud)}`;
    let c = map.get(key);
    if (!c) {
      c = {
        key,
        groupKey: `${norm(stamp)}|${norm(tier)}|${norm(resource)}`,
        stamp,
        tier,
        resource,
        resourceNorm: norm(resource),
        cloudResource: cloud,
        qty: null,
        altCloudResource: '',
        altQty: null,
        osDiskType: '',
        osDiskGb: null,
        osDiskMixed: false,
        osDiskSnapshots: null,
        lineCount: 0,
        volumes: [],
      };
      map.set(key, c);
    }
    c.lineCount += 1;
    const q = parseNum(l.quantity);
    if (q !== null) c.qty = (c.qty || 0) + q;
    if (!c.altCloudResource && clean(l.altCloudResource)) c.altCloudResource = clean(l.altCloudResource);
    const aq = parseNum(l.altQuantity);
    if (aq !== null) c.altQty = (c.altQty || 0) + aq;
    const dt = clean(l.osDiskType);
    const dg = parseNum(l.osDiskGb);
    if (dt && !c.osDiskType) c.osDiskType = dt;
    else if (dt && norm(dt) !== norm(c.osDiskType)) c.osDiskMixed = true;
    if (dg !== null && c.osDiskGb === null) c.osDiskGb = dg;
    else if (dg !== null && dg !== c.osDiskGb) c.osDiskMixed = true;
    const snap = parseNum(l.osDiskSnapshots);
    if (snap !== null) c.osDiskSnapshots = (c.osDiskSnapshots || 0) + snap;
  });
  return [...map.values()];
}

// The Storage Bill of Materials names volumes; it does not carry a foreign key
// to a compute line. Joining the two is therefore an inference, and it is
// treated as one: an attachment is only made when exactly one resource matches,
// the rule that produced it is carried on the volume, and everything ambiguous
// or unmatched is reported instead of being absorbed.
const IOPS_KEYS = ['iops', 'provisionedIops', 'iopsProvisioned', 'provisionedIOPS'];
const THROUGHPUT_KEYS = ['throughput', 'throughputMbps', 'mbps', 'throughputMBs'];

function firstNum(obj, keys) {
  for (let i = 0; i < keys.length; i += 1) {
    const v = obj ? obj[keys[i]] : undefined;
    const n = parseNum(v);
    if (n !== null) return n;
  }
  return null;
}

function attachVolumes(cards, csg) {
  const lines = (csg && csg.storageLines) || [];
  const unattached = [];
  const byGroup = new Map();
  cards.forEach((c) => {
    if (!byGroup.has(c.groupKey)) byGroup.set(c.groupKey, []);
    byGroup.get(c.groupKey).push(c);
  });

  lines.forEach((l, i) => {
    const desc = clean(l.epicResource);
    const tier = clean(l.epicTier);
    const stamp = clean(l.stamp);
    const vol = {
      id: `v-${i}`,
      desc: desc || '(volume not named)',
      diskType: clean(l.storageType),
      capacityGb: parseNum(l.capacityGb),
      quantity: parseNum(l.quantity),
      iops: firstNum(l, IOPS_KEYS),
      throughput: firstNum(l, THROUGHPUT_KEYS),
      tier,
      stamp,
      how: '',
      reason: '',
    };

    // Candidates: constrained by whatever the storage sheet actually states.
    let pool = cards;
    if (tier) pool = pool.filter((c) => norm(c.tier) === norm(tier));
    if (stamp) pool = pool.filter((c) => norm(c.stamp) === norm(stamp));
    const dn = norm(desc);

    let best = 0;
    let hits = [];
    if (dn) {
      pool.forEach((c) => {
        if (!c.resourceNorm) return;
        let score = 0;
        if (c.resourceNorm === dn) score = 1000;
        else if (dn.indexOf(c.resourceNorm) >= 0 && c.resourceNorm.length >= 4) score = c.resourceNorm.length;
        else if (c.resourceNorm.indexOf(dn) >= 0 && dn.length >= 4) score = dn.length;
        if (!score) return;
        if (score > best) { best = score; hits = [c]; }
        else if (score === best) hits.push(c);
      });
    }

    // A tie across stamps is the common case: the same Epic Resource exists in
    // Production and in Build. If the volume description names one of those
    // stamps it resolves itself — but only once the resource name is taken out
    // of the description first, or "Build ODB Production Server" would read as
    // naming Production. The longest stamp name wins what is left, so
    // "Alternate Production" beats the "Production" inside it. If that still
    // does not single one out, the workbook has not said, and nothing is
    // attached.
    let stampNamed = false;
    if (hits.length > 1) {
      const named = hits
        .map((c) => {
          const rest = c.resourceNorm ? dn.replace(c.resourceNorm, ' ') : dn;
          const sn = norm(c.stamp);
          return { card: c, sn, ok: !!sn && rest.indexOf(sn) >= 0 };
        })
        .filter((x) => x.ok);
      if (named.length) {
        const longest = named.reduce((a, b) => (b.sn.length > a.sn.length ? b : a));
        const winners = named.filter((x) => x.sn.length === longest.sn.length);
        if (winners.length === 1) { hits = [winners[0].card]; stampNamed = true; }
      }
    }

    if (hits.length === 1) {
      const c = hits[0];
      vol.how = best === 1000
        ? 'volume description equals the Epic Resource name'
        : 'volume description contains the Epic Resource name';
      if (stampNamed) vol.how += `, and names the "${c.stamp}" stamp`;
      if (tier || stamp) vol.how += `, within the ${[tier, stamp].filter(Boolean).join(' / ')} it states`;
      c.volumes.push(vol);
      return;
    }

    vol.reason = !dn
      ? 'the storage line names no volume, so there is nothing to match on'
      : hits.length > 1
        ? `the description matches ${hits.length} resources (${[...new Set(hits.map((c) => `${c.resource} / ${c.stamp}`))].slice(0, 3).join('; ')}) and the workbook does not say which`
        : 'no Epic Resource on the compute sheet carries this wording';
    unattached.push(vol);
  });

  return unattached;
}

// The accent a card wears: the loudest disk on it. A card whose data volumes are
// io2 reads as io2 even if its OS disk is gp3, because that is the fact that
// drives the build.
function cardAccent(c) {
  const styles = [diskStyle(c.osDiskType), ...c.volumes.map((v) => diskStyle(v.diskType))];
  return styles.reduce((a, b) => (b.rank > a.rank ? b : a), DISK_UNKNOWN);
}

function volumeSpec(v) {
  const st = diskStyle(v.diskType);
  const bits = [v.diskType || 'disk type not stated'];
  bits.push(v.capacityGb === null ? 'capacity not stated' : `${fmt(v.capacityGb)} GB`);
  if (v.quantity !== null && v.quantity > 1) bits.push(`× ${fmt(v.quantity)}`);
  if (v.iops !== null) bits.push(`${fmt(v.iops)} IOPS`);
  else if (st.provisioned) bits.push('IOPS not supplied');
  if (v.throughput !== null) bits.push(`${fmt(v.throughput)} MB/s`);
  return bits.join(' · ');
}

function cardHover(c) {
  const lines = [
    c.resource || '(Epic Resource not named)',
    `Stamp: ${c.stamp}`,
    `Epic Tier: ${c.tier}`,
    `Cloud resource: ${c.cloudResource || 'not stated in the workbook'}`,
    `Quantity: ${c.qty === null ? 'not stated in the workbook' : fmt(c.qty)}`,
    c.altCloudResource
      ? `Alternate sizing: ${c.altCloudResource}${c.altQty === null ? '' : ` × ${fmt(c.altQty)}`}`
      : 'Alternate sizing: none in this workbook',
    `OS disk: ${c.osDiskType || 'type not stated'} · ${c.osDiskGb === null ? 'capacity not stated' : `${fmt(c.osDiskGb)} GB`}${c.osDiskMixed ? ' (lines disagree — see the bill of materials table)' : ''}`,
    'vCPU / RAM: not stated by the CSG for any instance type — never inferred here',
  ];
  if (c.volumes.length) {
    lines.push(`Volumes attached (${c.volumes.length}, inferred by name):`);
    c.volumes.forEach((v) => lines.push(`  • ${v.desc} — ${volumeSpec(v)}`));
  } else {
    lines.push('Volumes attached: none matched from the Storage Bill of Materials');
  }
  if (c.changeNote) lines.push(`Change: ${c.changeNote}`);
  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Version comparison.
//
// The CSG screen diffs line items keyed on tier + stamp + resource + cloud
// resource, which is right for a line-by-line audit but reports a re-sized
// instance as one Removed plus one Added. On a drawing, "this resource moved
// from m5.8xlarge to r5.8xlarge" is one fact about one box, so the pairing is
// done here: an Added and a Removed alone in the same tier/stamp/resource group
// become a single Retyped card carrying both sides.
// ---------------------------------------------------------------------------
function diffCards(baseCards, nextCards, baseLabel) {
  const baseByKey = new Map(baseCards.map((c) => [c.key, c]));
  const nextByKey = new Map(nextCards.map((c) => [c.key, c]));
  const out = [];

  nextCards.forEach((c) => {
    const prev = baseByKey.get(c.key);
    if (!prev) { out.push({ ...c, status: 'Added', changeNote: `not present in ${baseLabel}` }); return; }
    const a = prev.qty === null ? null : prev.qty;
    const b = c.qty === null ? null : c.qty;
    if (a === null || b === null) {
      out.push({
        ...c,
        status: a === b ? 'Unchanged' : 'Resized',
        prevQty: a,
        changeNote: a === b ? '' : `quantity ${a === null ? 'not stated' : fmt(a)} → ${b === null ? 'not stated' : fmt(b)}`,
      });
      return;
    }
    const d = b - a;
    out.push({
      ...c,
      status: d === 0 ? 'Unchanged' : 'Resized',
      prevQty: a,
      delta: d,
      changeNote: d === 0 ? '' : `quantity ${fmt(a)} → ${fmt(b)} (${d > 0 ? '+' : ''}${d})`,
    });
  });

  baseCards.forEach((c) => {
    if (nextByKey.has(c.key)) return;
    out.push({ ...c, status: 'Removed', fromBaseline: true, changeNote: `present in ${baseLabel}, gone from this one` });
  });

  // Pair a lone Added with a lone Removed in the same group → one Retyped card.
  const groups = new Map();
  out.forEach((c) => {
    if (c.status !== 'Added' && c.status !== 'Removed') return;
    if (!groups.has(c.groupKey)) groups.set(c.groupKey, { added: [], removed: [] });
    const g = groups.get(c.groupKey);
    if (c.status === 'Added') g.added.push(c); else g.removed.push(c);
  });
  const drop = new Set();
  groups.forEach((g) => {
    if (g.added.length !== 1 || g.removed.length !== 1) return;
    const a = g.added[0];
    const r = g.removed[0];
    a.status = 'Retyped';
    a.prevCloudResource = r.cloudResource;
    a.prevQty = r.qty;
    const qtyPart = (a.qty === r.qty) ? '' : `, quantity ${r.qty === null ? 'not stated' : fmt(r.qty)} → ${a.qty === null ? 'not stated' : fmt(a.qty)}`;
    a.changeNote = `instance type ${r.cloudResource || 'not stated'} → ${a.cloudResource || 'not stated'}${qtyPart}`;
    drop.add(r.key);
  });

  return out.filter((c) => !(drop.has(c.key) && c.status === 'Removed'));
}

// ---------------------------------------------------------------------------
// Layout — measured, so nothing ever overlaps anything
// ---------------------------------------------------------------------------
function measureCard(c) {
  const nameLines = wrapLabel(c.resource || c.cloudResource || '(resource not named)', 34, 2);
  let h = 7;
  h += nameLines.length * 12;
  h += 15;                                   // instance type + quantity
  h += 13;                                   // OS disk
  if (c.altCloudResource) h += 12;
  if (c.volumes.length) h += 5 + c.volumes.length * 13;
  if (c.changeNote) h += 12;
  h += 6;
  return { nameLines, h: Math.max(52, h) };
}

function buildLayout(cards, cellUnattached, stamps, tiers, overallText) {
  const measured = cards.map((c) => ({ ...c, ...measureCard(c), accent: cardAccent(c) }));

  const cellKey = (stamp, tier) => `${norm(stamp)}||${norm(tier)}`;
  const byCell = new Map();
  measured.forEach((c) => {
    const k = cellKey(c.stamp, c.tier);
    if (!byCell.has(k)) byCell.set(k, []);
    byCell.get(k).push(c);
  });
  byCell.forEach((list) => list.sort((a, b) => (b.qty || 0) - (a.qty || 0) || String(a.resource).localeCompare(String(b.resource))));

  const colX = new Map();
  stamps.forEach((s, i) => colX.set(s, ROW_HEAD_W + i * (COL_W + COL_GAP)));

  const rows = [];
  let y = STAMP_HEAD_H + ROW_GAP;
  tiers.forEach((t) => {
    let h = EMPTY_CELL_H + CELL_PAD * 2;
    stamps.forEach((s) => {
      const list = byCell.get(cellKey(s, t)) || [];
      const extra = (cellUnattached.get(cellKey(s, t)) || []).length;
      const inner = list.length
        ? list.reduce((a, c) => a + c.h, 0) + (list.length - 1) * CARD_GAP
        : EMPTY_CELL_H;
      h = Math.max(h, inner + CELL_PAD * 2 + (extra ? 14 + extra * 12 : 0));
    });
    rows.push({ tier: t, y, h });
    y += h + ROW_GAP;
  });

  const placed = [];
  rows.forEach((r) => {
    stamps.forEach((s) => {
      const list = byCell.get(cellKey(s, r.tier)) || [];
      let cy = r.y + CELL_PAD;
      list.forEach((c) => {
        placed.push({ ...c, x: colX.get(s) + CELL_PAD, y: cy, w: CARD_W });
        cy += c.h + CARD_GAP;
      });
    });
  });

  const cells = [];
  rows.forEach((r) => {
    stamps.forEach((s) => {
      const list = byCell.get(cellKey(s, r.tier)) || [];
      cells.push({
        stamp: s,
        tier: r.tier,
        x: colX.get(s),
        y: r.y,
        w: COL_W,
        h: r.h,
        count: list.length,
        unattached: cellUnattached.get(cellKey(s, r.tier)) || [],
        cardsBottom: list.length
          ? r.y + CELL_PAD + list.reduce((a, c) => a + c.h, 0) + (list.length - 1) * CARD_GAP
          : r.y + CELL_PAD,
      });
    });
  });

  const totalsY = y + 6;
  const overallY = totalsY + TOTALS_H + 10;
  const width = ROW_HEAD_W + stamps.length * (COL_W + COL_GAP) - COL_GAP;

  // The whole-CSG band carries long sentences and a one-stamp CSG (an Isolated
  // Recovery workbook is often exactly that) leaves it barely 400 px wide, so
  // the band is wrapped to its own width and grows rather than spilling text
  // past its border.
  const overallLines = [];
  (overallText || []).forEach((t) => {
    const per = Math.max(34, Math.floor((width - 26) / (t.style === 'title' ? 5.9 : 5.0)));
    wrapLabel(t.text, per, 5).forEach((l) => overallLines.push({ text: l, style: t.style }));
  });
  const overallH = Math.max(OVERALL_H, 12 + overallLines.length * 14 + 8);
  const height = overallY + overallH;

  return { cards: placed, cells, rows, stamps, tiers, colX, totalsY, overallY, overallLines, overallH, width, height };
}

// ---------------------------------------------------------------------------
// SVG. No foreignObject, no external fonts, no network requests: it has to
// rasterise onto a canvas and survive a print dialog.
// ---------------------------------------------------------------------------
function svgText(x, y, s, size, color, weight, anchor) {
  return `<text x="${x}" y="${y}" font-family="${FONT}" font-size="${size}" fill="${color}"${weight ? ` font-weight="${weight}"` : ''}${anchor ? ` text-anchor="${anchor}"` : ''}>${svgEsc(s)}</text>`;
}

function svgCard(c, selectedKey) {
  const st = c.accent;
  const ch = CHANGE_STYLES[c.status] || null;
  const selected = c.key === selectedKey;
  const dim = c.dimmed && !ch;
  const stroke = selected ? NAVY : (ch ? ch.color : (dim ? '#d7dee6' : st.color));
  const p = [`<g data-card="${svgEsc(c.key)}" style="cursor:pointer">`];
  p.push(`<title>${svgEsc(c.hover)}</title>`);
  p.push(`<rect x="${c.x}" y="${c.y}" width="${c.w}" height="${c.h}" rx="5" fill="${ch ? ch.fill : '#ffffff'}" stroke="${stroke}" stroke-width="${selected ? 2.6 : 1.4}"${c.status === 'Removed' ? ' stroke-dasharray="6 4"' : ''}/>`);
  p.push(`<rect x="${c.x}" y="${c.y + 4}" width="4" height="${c.h - 8}" rx="2" fill="${st.color}"${st.dashed ? ' fill-opacity="0.5"' : ''}/>`);

  let cur = c.y + 7;
  const nameColor = dim ? GREY : NAVY;
  c.nameLines.forEach((l) => {
    p.push(svgText(c.x + 12, cur + 9, l, 10, nameColor, 600));
    cur += 12;
  });

  // Instance type + quantity. The quantity pill is the only number on the card
  // the workbook always supplies; when it does not, it says so.
  const qtyText = c.qty === null ? 'qty not stated' : `× ${fmt(c.qty)}`;
  const pillW = c.qty === null ? 74 : 46;
  p.push(`<rect x="${c.x + c.w - pillW - 8}" y="${cur + 1}" width="${pillW}" height="14" rx="3" fill="${c.qty === null ? '#f4f6f8' : (dim ? '#eef1f5' : '#e8f0fe')}" stroke="${c.qty === null ? '#cfd8dc' : (dim ? '#dde3ea' : '#bcd4ee')}" stroke-width="1"/>`);
  p.push(svgText(c.x + c.w - pillW / 2 - 8, cur + 11.5, qtyText, c.qty === null ? 8 : 9.5, c.qty === null ? GREY : (dim ? GREY : BLUE), 700, 'middle'));
  p.push(svgText(c.x + 12, cur + 11, trunc(c.cloudResource || 'cloud resource not stated', 28), 10.5, c.cloudResource ? (dim ? GREY : NAVY) : GREY, 700));
  cur += 15;

  const osStyle = diskStyle(c.osDiskType);
  const osText = `OS disk: ${c.osDiskType || 'type not stated'} · ${c.osDiskGb === null ? 'capacity not stated' : `${fmt(c.osDiskGb)} GB`}${c.osDiskMixed ? ' (mixed)' : ''}`;
  p.push(`<rect x="${c.x + 12}" y="${cur + 1}" width="7" height="7" rx="1.5" fill="${osStyle.fill}" stroke="${osStyle.color}" stroke-width="1"${osStyle.dashed ? ' stroke-dasharray="2 1.5"' : ''}/>`);
  p.push(svgText(c.x + 23, cur + 8, trunc(osText, 55), 8.5, c.osDiskType ? GREY : '#8a5a00'));
  cur += 13;

  if (c.altCloudResource) {
    p.push(svgText(c.x + 12, cur + 8, trunc(`alternate sizing in the workbook: ${c.altCloudResource}${c.altQty === null ? '' : ` × ${fmt(c.altQty)}`}`, 58), 8, GREY));
    cur += 12;
  }

  if (c.volumes.length) {
    p.push(`<line x1="${c.x + 12}" y1="${cur + 2}" x2="${c.x + c.w - 8}" y2="${cur + 2}" stroke="#e6ebf1" stroke-width="1"/>`);
    cur += 5;
    c.volumes.forEach((v) => {
      const vs = diskStyle(v.diskType);
      p.push(`<rect x="${c.x + 12}" y="${cur + 2}" width="7" height="8" rx="1.5" fill="${vs.fill}" stroke="${vs.color}" stroke-width="${vs.provisioned ? 1.6 : 1}"${vs.dashed ? ' stroke-dasharray="2 1.5"' : ''}/>`);
      p.push(svgText(c.x + 23, cur + 9, trunc(`${trunc(v.desc, 20)} · ${volumeSpec(v)}`, 55), 8.5, vs.provisioned ? '#9d2020' : GREY, vs.provisioned ? 600 : 400));
      cur += 13;
    });
  }

  if (c.changeNote) {
    p.push(svgText(c.x + 12, cur + 8, trunc(c.changeNote, 58), 8, ch ? ch.color : GREY, 600));
    cur += 12;
  }

  if (ch) {
    const bw = ch.badge.length * 5.4 + 10;
    p.push(`<rect x="${c.x + c.w - bw - 6}" y="${c.y - 7}" width="${bw}" height="14" rx="3" fill="${ch.color}"/>`);
    p.push(svgText(c.x + c.w - bw / 2 - 6, c.y + 3, ch.badge, 8.5, '#ffffff', 700, 'middle'));
  }
  p.push('</g>');
  return p.join('');
}

function svgTotalsBox(x, y, w, h, title, rows, accent) {
  const p = [`<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="5" fill="#f7f9fc" stroke="${accent || '#d7dee6'}" stroke-width="1.2"/>`];
  p.push(svgText(x + 10, y + 15, title, 10, GREY, 700));
  rows.forEach((r, i) => {
    const by = y + 31 + i * 15;
    p.push(svgText(x + 10, by, r.label, 9.5, GREY));
    p.push(svgText(x + w - 10, by, r.value, 10.5, r.muted ? GREY : NAVY, 700, 'end'));
  });
  return p.join('');
}

function svgGrid(layout, model, opts) {
  const p = [];
  const { stamps, colX } = layout;

  // Stamp headers — the columns are Epic's stamps, in Epic's order.
  stamps.forEach((s) => {
    const x = colX.get(s);
    const t = model.stampTotals.get(s) || {};
    p.push(`<rect x="${x}" y="0" width="${COL_W}" height="${STAMP_HEAD_H}" rx="5" fill="${NAVY}"/>`);
    p.push(svgText(x + 12, 20, trunc(s, 30), 12.5, '#ffffff', 700));
    p.push(svgText(x + 12, 35, `${fmt(t.cardCount || 0)} resource group(s) · ${t.instances === null ? 'quantity not stated' : `${fmt(t.instances)} instance(s)`}`, 9, '#d9e6f4'));
  });
  p.push(svgText(6, 27, 'EPIC TIER', 9.5, GREY, 700));
  p.push(svgText(6, 39, 'STAMP →', 9.5, GREY, 700));

  // Rows: the Epic tiers.
  layout.rows.forEach((r) => {
    p.push(`<rect x="0" y="${r.y}" width="${ROW_HEAD_W - 8}" height="${r.h}" rx="5" fill="#eef3f9" stroke="#dbe4ee" stroke-width="1"/>`);
    const lines = wrapLabel(r.tier, 17, 4);
    const startY = r.y + r.h / 2 - ((lines.length - 1) * 12) / 2 + 4;
    lines.forEach((l, i) => p.push(svgText(10, startY + i * 12, l, 10.5, NAVY, 700)));
    const tt = model.tierTotals.get(r.tier) || {};
    p.push(svgText(10, r.y + r.h - 8, `${tt.instances === null ? 'qty n/s' : `${fmt(tt.instances)} inst.`}`, 9, GREY));
  });

  // Cells.
  layout.cells.forEach((c) => {
    p.push(`<rect x="${c.x}" y="${c.y}" width="${c.w}" height="${c.h}" rx="5" fill="${c.count ? '#fbfcfe' : '#fafbfc'}" stroke="#e6ebf1" stroke-width="1"/>`);
    if (!c.count) {
      p.push(`<rect x="${c.x + CELL_PAD}" y="${c.y + CELL_PAD}" width="${CARD_W}" height="${EMPTY_CELL_H}" rx="4" fill="#ffffff" stroke="#dfe5ec" stroke-width="1" stroke-dasharray="5 4"/>`);
      p.push(svgText(c.x + CELL_PAD + CARD_W / 2, c.y + CELL_PAD + 20, 'no line in this CSG for this tier + stamp', 8.5, GREY, 400, 'middle'));
    }
    if (c.unattached.length) {
      let uy = c.cardsBottom + 12;
      p.push(svgText(c.x + CELL_PAD + 2, uy, `${c.unattached.length} volume(s) in this cell not matched to a named resource:`, 8, '#8a5a00', 600));
      c.unattached.forEach((v) => {
        uy += 12;
        p.push(svgText(c.x + CELL_PAD + 8, uy, trunc(`• ${v.desc} — ${volumeSpec(v)}`, 54), 8, '#8a5a00'));
      });
    }
  });

  // Cards.
  layout.cards.forEach((c) => p.push(svgCard(c, opts.selectedKey)));

  // Per-stamp totals, then the overall band.
  stamps.forEach((s) => {
    const t = model.stampTotals.get(s) || {};
    p.push(svgTotalsBox(colX.get(s), layout.totalsY, COL_W, TOTALS_H, `TOTALS — ${trunc(s, 26)}`, [
      { label: 'Instances', value: t.instances === null ? DASH : fmt(t.instances) },
      { label: 'OS disk capacity', value: t.osDiskGb === null ? `${DASH} not supplied` : `${fmt(t.osDiskGb)} GB`, muted: t.osDiskGb === null },
      { label: 'Attached volume capacity', value: t.volumeGb === null ? `${DASH} not supplied` : `${fmt(t.volumeGb)} GB`, muted: t.volumeGb === null },
      { label: 'Provisioned IOPS', value: t.iops === null ? `${DASH} not supplied` : fmt(t.iops), muted: t.iops === null },
    ]));
  });
  p.push(svgText(6, layout.totalsY + 18, 'TOTALS', 10, GREY, 700));
  p.push(svgText(6, layout.totalsY + 32, 'per stamp', 9, GREY));
  p.push(svgText(6, layout.totalsY + 46, 'and overall', 9, GREY));

  p.push(`<rect x="0" y="${layout.overallY}" width="${layout.width}" height="${layout.overallH}" rx="5" fill="#eef3f9" stroke="${NAVY}" stroke-width="1.4"/>`);
  layout.overallLines.forEach((l, i) => {
    const title = l.style === 'title';
    p.push(svgText(12, layout.overallY + 18 + i * 14, l.text, title ? 11 : 9.5, l.style === 'muted' ? GREY : NAVY, title ? 700 : undefined));
  });

  return p.join('');
}

// Legend drawn into the SVG so a PNG stands alone once it leaves the room.
function svgLegend(x, y, width, L) {
  const cols = Math.max(1, Math.floor(width / 320));
  const parts = [svgText(x, y + 12, 'Legend', 12, NAVY, 700)];
  let cursor = y + 26;
  const perLine = Math.max(60, Math.floor(width / 5.1));
  const note = (t) => {
    wrapLabel(t, perLine, 4).forEach((l) => {
      parts.push(svgText(x, cursor + 8, l, 10, GREY));
      cursor += 13;
    });
    cursor += 5;
  };

  parts.push(svgText(x, cursor + 9, 'DISK TYPE (card accent bar and volume swatch)', 10, GREY, 700));
  cursor += 18;
  L.diskRows.forEach((d, i) => {
    const c = i % cols;
    const r = Math.floor(i / cols);
    const bx = x + c * 320;
    const by = cursor + r * 18;
    parts.push(`<rect x="${bx}" y="${by}" width="20" height="13" rx="3" fill="${d.fill}" stroke="${d.color}" stroke-width="${d.provisioned ? 2 : 1.5}"${d.dashed ? ' stroke-dasharray="4 2.5"' : ''}/>`);
    parts.push(svgText(bx + 26, by + 10.5, trunc(`${d.label} (${d.count})`, LEGEND_CHARS), 10, NAVY));
  });
  cursor += Math.ceil(L.diskRows.length / cols) * 18 + 6;
  note('io1 / io2 buy provisioned IOPS and gp3 does not — the single most cost- and performance-significant choice in an Epic build, so it is the loudest mark on the page.');

  if (L.compare) {
    parts.push(svgText(x, cursor + 9, `CHANGES SINCE ${L.baseLabel.toUpperCase()}`, 10, GREY, 700));
    cursor += 18;
    L.changeRows.forEach((r, i) => {
      const c = i % cols;
      const rr = Math.floor(i / cols);
      const bx = x + c * 320;
      const by = cursor + rr * 18;
      const style = CHANGE_STYLES[r.status];
      parts.push(`<rect x="${bx}" y="${by}" width="20" height="13" rx="3" fill="${style.fill}" stroke="${style.color}" stroke-width="1.5"${r.status === 'Removed' ? ' stroke-dasharray="4 2.5"' : ''}/>`);
      parts.push(svgText(bx + 26, by + 10.5, trunc(`${r.label} (${r.count})`, LEGEND_CHARS), 10, NAVY));
    });
    cursor += Math.ceil(L.changeRows.length / cols) * 18 + 6;
    note(`Removed resources are drawn as dashed ghosts so the change is visible; they are NOT counted in any total on this page — every total is ${L.csgLabel}.`);
  }

  parts.push(svgText(x, cursor + 9, 'WHAT A CARD SHOWS', 10, GREY, 700));
  cursor += 16;
  [
    'Epic Resource name, then the cloud resource (AWS instance type) and its quantity, exactly as the Compute Bill of Materials states them.',
    'OS disk type and capacity from the same row; then each Storage Bill of Materials volume matched to that resource, with disk type, capacity and provisioned IOPS where the workbook gives them.',
    `Volumes are attached by matching the volume description to the Epic Resource name — an INFERENCE, not a key in the workbook. ${L.attachedCount} volume(s) attached, ${L.unattachedCount} left unattached and listed rather than absorbed.`,
    `"${DASH}" means the workbook did not supply that figure. vCPU and RAM appear nowhere on this page: the CSG states instance types, not their sizing, and inferring it from a type name would be inventing the client's architecture.`,
    'The CSG is a point-in-time draft and gets reissued. This drawing is only as current as the import it was drawn from.',
  ].forEach(note);

  parts.push(svgText(x, cursor + 9, `${L.cardCount} resource group(s) drawn from ${L.lineCount} compute line(s) · ${L.instances} instance(s) · ${L.stamps} stamp(s) × ${L.tiers} tier(s)`, 11, NAVY, 700));
  cursor += 16;

  return { markup: parts.join(''), height: cursor - y };
}

function buildSvg(layout, model, opts) {
  const o = opts || {};
  const pad = 26;
  const contentW = Math.max(layout.width, o.header || o.legend ? 720 : 0);

  const body = ['<rect x="0" y="0" width="__W__" height="__H__" fill="#ffffff"/>'];

  // The header's scope and summary lines are long sentences and the grid can be
  // one narrow column wide, so they wrap to the page rather than run off it.
  let headerH = 0;
  if (o.header) {
    const meta = o.meta || {};
    const scopeLines = wrapLabel(meta.scope || '', Math.max(40, Math.floor(contentW / 5.6)), 3);
    const subLines = wrapLabel(meta.sub || '', Math.max(40, Math.floor(contentW / 5.1)), 5);
    body.push(svgText(pad, pad + 6, meta.title || 'Primordia — Target Architecture', 16, NAVY, 700));
    let hy = pad + 25;
    scopeLines.forEach((l) => { body.push(svgText(pad, hy, l, 11.5, BLUE)); hy += 15; });
    hy += 2;
    subLines.forEach((l) => { body.push(svgText(pad, hy, l, 10.5, GREY)); hy += 13; });
    headerH = hy - pad + 8;
  }

  body.push(`<g transform="translate(${pad},${pad + headerH})">`);
  body.push(svgGrid(layout, model, { selectedKey: o.selectedKey || '' }));
  body.push('</g>');

  let legendH = 0;
  if (o.legend && o.legendModel) {
    const lg = svgLegend(pad, pad + headerH + layout.height + 26, Math.max(contentW, 660), o.legendModel);
    legendH = lg.height + 26;
    body.push(lg.markup);
  }

  const width = Math.round(Math.max(contentW, 660) + pad * 2);
  const height = Math.round(layout.height + headerH + legendH + pad * 2);
  const inner = body.join('').replace('__W__', String(width)).replace('__H__', String(height));
  return { inner, width, height };
}

const svgDoc = (svg, renderW, renderH) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${svg.width} ${svg.height}" width="${Math.round(renderW)}" height="${Math.round(renderH)}">${svg.inner}</svg>`;

// Print CSS rides inside the printed block, so exports.js keeps owning the print
// window and we still get landscape paper.
const PRINT_CSS = `
  @page { size: landscape; margin: 10mm; }
  body { margin: 0 !important; font-family: ${FONT}; color: #16202c; }
  .ta-title { display: flex; align-items: center; gap: 10px; border-bottom: 2px solid ${NAVY}; padding-bottom: 8px; margin-bottom: 10px; }
  .ta-title h1 { font-size: 17px; margin: 0; color: ${NAVY}; }
  .ta-title .sub { font-size: 11.5px; color: ${BLUE}; margin-top: 2px; }
  .ta-meta { display: flex; flex-wrap: wrap; gap: 4px 22px; font-size: 10.5px; color: ${GREY}; margin-bottom: 10px; }
  .ta-meta b { color: ${NAVY}; }
  .ta-note { font-size: 10.5px; color: #8a5a00; background: #fff8e6; border: 1px solid #f0d9a8; padding: 6px 8px; border-radius: 4px; margin-bottom: 10px; }
  .ta-legend { border: 1px solid #d7dee6; border-radius: 4px; padding: 8px 10px; margin: 10px 0; font-size: 10.5px; page-break-inside: avoid; }
  .ta-legend h2 { font-size: 12px; margin: 0 0 6px; color: ${NAVY}; }
  .ta-legend h3 { font-size: 10px; margin: 8px 0 4px; color: ${GREY}; letter-spacing: .4px; }
  .ta-legend .items { display: flex; flex-wrap: wrap; gap: 4px 16px; }
  .ta-legend .it { display: flex; align-items: center; gap: 6px; width: 300px; }
  .ta-legend .sw { display: inline-block; width: 20px; height: 13px; border-radius: 3px; }
  .ta-page { page-break-after: always; }
  .ta-page:last-child { page-break-after: auto; }
  .ta-mappage { page-break-before: always; }
  .ta-tile { overflow: hidden; position: relative; }
  .ta-tile > div { position: absolute; }
  .ta-cap { font-size: 10px; color: ${GREY}; margin-top: 4px; }
  table.ta-tbl { border-collapse: collapse; width: 100%; font-size: 9.5px; }
  table.ta-tbl th { text-align: left; color: ${NAVY}; border-bottom: 1px solid #c8d2dd; padding: 3px 5px; }
  table.ta-tbl td { border-bottom: 1px solid #eef1f5; padding: 3px 5px; vertical-align: top; }
  h2.ta-h { font-size: 13px; color: ${NAVY}; margin: 12px 0 6px; }
`;

// ---------------------------------------------------------------------------
export default function TargetArchitecture({ tenantId }) {
  const lsKey = `primordia.csg.${tenantId}`;

  const [db, setDb] = useState(null);
  const [scopeSaved, setScopeSaved] = useState(false);
  const [tenantName, setTenantName] = useState(tenantId);

  const [csgId, setCsgId] = useState('');
  const [baseId, setBaseId] = useState(NONE);
  const [dimUnchanged, setDimUnchanged] = useState(true);
  const [showCurrent, setShowCurrent] = useState(false);
  const [zoom, setZoom] = useState(0.6);
  const [pageMode, setPageMode] = useState('fit');
  const [selectedKey, setSelectedKey] = useState('');

  const [servers, setServers] = useState(null);
  const [serversErr, setServersErr] = useState('');

  // ---- Load: the CSG store is read strictly read-only ------------------------
  const reload = useCallback(() => {
    setDb(readDb(lsKey));
    let saved = false;
    try {
      const raw = localStorage.getItem(lsKey);
      const parsed = raw ? JSON.parse(raw) : null;
      saved = !!(parsed && parsed.scope);
    } catch (e) { saved = false; }
    setScopeSaved(saved);
  }, [lsKey]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    setCsgId('');
    setBaseId(NONE);
    setSelectedKey('');
    setServers(null);
    setServersErr('');
  }, [tenantId]);

  useEffect(() => {
    let live = true;
    store.listTenants().then((list) => {
      if (!live) return;
      const t = (list || []).find((x) => x.id === tenantId);
      setTenantName(t ? t.name : tenantId);
    }).catch(() => { /* the header falls back to the id */ });
    return () => { live = false; };
  }, [tenantId]);

  // Current-state context is optional, so its 7,000-row load only happens when
  // somebody actually asks for it.
  useEffect(() => {
    if (!showCurrent) return undefined;
    let live = true;
    setServersErr('');
    store.listFactSheets(tenantId, { type: 'ITComponent' })
      .then((rows) => { if (live) setServers(rows || []); })
      .catch(() => { if (live) setServersErr('Could not load the current-state servers for this workspace.'); });
    return () => { live = false; };
  }, [showCurrent, tenantId]);

  const csgs = useMemo(() => (db && db.csgs) || [], [db]);

  useEffect(() => {
    if (!csgs.length) return;
    setCsgId((cur) => (csgs.some((c) => c.id === cur) ? cur : csgs[0].id));
    setBaseId((cur) => (cur !== NONE && csgs.some((c) => c.id === cur) ? cur : NONE));
  }, [csgs]);

  const csg = useMemo(() => csgs.find((c) => c.id === csgId) || csgs[0] || null, [csgs, csgId]);
  const baseCsg = useMemo(
    () => (baseId === NONE ? null : csgs.find((c) => c.id === baseId) || null),
    [csgs, baseId]
  );
  const comparing = !!(baseCsg && csg && baseCsg.id !== csg.id);

  // ---- Model -----------------------------------------------------------------
  const model = useMemo(() => {
    if (!csg) return null;

    const cards = foldCards(csg);
    const unattached = attachVolumes(cards, csg);

    let drawn = cards.map((c) => ({ ...c, status: '', changeNote: '' }));
    if (comparing) {
      const baseCards = foldCards(baseCsg);
      attachVolumes(baseCards, baseCsg);
      drawn = diffCards(baseCards, cards, `"${baseCsg.label}"`);
    }
    drawn = drawn.map((c) => ({ ...c, hover: cardHover(c) }));

    const stamps = orderBy([...new Set(drawn.map((c) => c.stamp))], STAMP_ORDER, NOT_STATED_STAMP);
    const tiers = orderBy([...new Set(drawn.map((c) => c.tier))], TIER_ORDER, NOT_STATED_TIER);

    // Unattached volumes land in their own tier + stamp when the storage sheet
    // states them, and in a page-level list when it does not.
    const cellUnattached = new Map();
    const orphans = [];
    unattached.forEach((v) => {
      if (v.tier && v.stamp) {
        const k = `${norm(v.stamp)}||${norm(v.tier)}`;
        if (!cellUnattached.has(k)) cellUnattached.set(k, []);
        cellUnattached.get(k).push(v);
      } else {
        orphans.push(v);
      }
    });

    // Totals. Instance counts and OS disk capacity come from csgTotals so the
    // arithmetic is identical to the CSG screen's, run over the stamp's own
    // lines. Volume capacity and IOPS are summed only over ATTACHED volumes for
    // a stamp, because an unattached volume has no stamp to belong to.
    const linesByStamp = new Map();
    ((csg && csg.lines) || []).forEach((l) => {
      const s = clean(l.stamp) || NOT_STATED_STAMP;
      if (!linesByStamp.has(s)) linesByStamp.set(s, []);
      linesByStamp.get(s).push(l);
    });

    const volumeTotals = (list) => {
      let gb = 0;
      let gbStated = 0;
      let iops = 0;
      let iopsStated = 0;
      list.forEach((v) => {
        const mult = v.quantity === null || v.quantity < 1 ? 1 : v.quantity;
        if (v.capacityGb !== null) { gb += v.capacityGb * mult; gbStated += 1; }
        if (v.iops !== null) { iops += v.iops * mult; iopsStated += 1; }
      });
      return {
        volumeGb: gbStated ? Math.round(gb) : null,
        iops: iopsStated ? Math.round(iops) : null,
      };
    };

    const currentCards = drawn.filter((c) => c.status !== 'Removed');
    const stampTotals = new Map();
    stamps.forEach((s) => {
      const t = csgTotals({ lines: linesByStamp.get(s) || [], storageLines: [] });
      const cellCards = currentCards.filter((c) => c.stamp === s);
      const vols = cellCards.reduce((a, c) => a.concat(c.volumes), []);
      const anyQty = (linesByStamp.get(s) || []).some((l) => parseNum(l.quantity) !== null);
      stampTotals.set(s, {
        cardCount: cellCards.length,
        instances: anyQty ? t.instances : null,
        osDiskGb: t.osDiskGb,
        ...volumeTotals(vols),
      });
    });

    const tierTotals = new Map();
    tiers.forEach((t) => {
      const cellCards = currentCards.filter((c) => c.tier === t);
      const anyQty = cellCards.some((c) => c.qty !== null);
      tierTotals.set(t, {
        instances: anyQty ? cellCards.reduce((a, c) => a + (c.qty || 0), 0) : null,
        cardCount: cellCards.length,
      });
    });

    const whole = csgTotals(csg);
    const allVols = currentCards.reduce((a, c) => a.concat(c.volumes), []);
    const overall = {
      ...whole,
      ...volumeTotals(allVols.concat(unattached)),
    };

    const changeCounts = ['Added', 'Removed', 'Resized', 'Retyped', 'Unchanged'].reduce((acc, k) => {
      acc[k] = drawn.filter((c) => c.status === k).length;
      return acc;
    }, {});

    const diskCounts = new Map();
    const bump = (style) => {
      const cur = diskCounts.get(style.key) || { ...style, count: 0 };
      cur.count += 1;
      diskCounts.set(style.key, cur);
    };
    currentCards.forEach((c) => {
      bump(diskStyle(c.osDiskType));
      c.volumes.forEach((v) => bump(diskStyle(v.diskType)));
    });
    unattached.forEach((v) => bump(diskStyle(v.diskType)));

    const overallText = [
      { style: 'title', text: `WHOLE CSG — ${csg.label} · ${csg.envKind}` },
      {
        style: 'body',
        text: `${overall.instances === null ? 'instance count not stated' : `${fmt(overall.instances)} instance(s)`} across ${stamps.length} stamp(s) and ${tiers.length} tier(s) · ${fmt(overall.distinctResources)} distinct cloud resource type(s) · ${fmt(whole.lineCount)} compute line(s) · ${fmt(whole.storageLineCount)} storage line(s)`,
      },
      {
        style: 'muted',
        text: `OS disk ${overall.osDiskGb === null ? `${DASH} not supplied` : `${fmt(overall.osDiskGb)} GB`} · Storage BoM capacity ${overall.storageGb === null ? `${DASH} not supplied` : `${fmt(overall.storageGb)} GB`} · Provisioned IOPS ${overall.iops === null ? `${DASH} not supplied by the workbook import` : fmt(overall.iops)} · vCPU / RAM ${DASH} the CSG does not state them, so they are never inferred here`,
      },
    ];

    return {
      cards: drawn,
      currentCards,
      overallText,
      stamps,
      tiers,
      cellUnattached,
      orphans,
      unattached,
      attachedCount: allVols.length,
      stampTotals,
      tierTotals,
      overall,
      changeCounts,
      diskRows: [...diskCounts.values()].sort((a, b) => b.rank - a.rank || b.count - a.count),
      csgLabel: csg.label,
      envKind: csg.envKind,
      lineCount: (csg.lines || []).length,
      storageLineCount: (csg.storageLines || []).length,
    };
  }, [csg, baseCsg, comparing]);

  const layout = useMemo(() => {
    if (!model || !model.cards.length) return null;
    const cards = model.cards.map((c) => ({ ...c, dimmed: comparing && dimUnchanged && (!c.status || c.status === 'Unchanged') }));
    return buildLayout(cards, model.cellUnattached, model.stamps, model.tiers, model.overallText);
  }, [model, comparing, dimUnchanged]);

  const legendModel = useMemo(() => {
    if (!model) return null;
    return {
      diskRows: model.diskRows,
      compare: comparing,
      baseLabel: comparing ? `"${baseCsg.label}"` : '',
      csgLabel: `"${model.csgLabel}"`,
      changeRows: ['Added', 'Removed', 'Resized', 'Retyped'].map((s) => ({
        status: s,
        count: model.changeCounts[s] || 0,
        label: s === 'Added' ? 'Added — no line in the earlier CSG'
          : s === 'Removed' ? 'Removed — earlier CSG only (ghost, uncounted)'
            : s === 'Resized' ? 'Quantity changed — same instance type'
              : 'Instance type changed — same Epic Resource',
      })),
      attachedCount: model.attachedCount,
      unattachedCount: model.unattached.length,
      cardCount: fmt(model.currentCards.length),
      lineCount: fmt(model.lineCount),
      instances: model.overall.instances === null ? DASH : fmt(model.overall.instances),
      stamps: model.stamps.length,
      tiers: model.tiers.length,
    };
  }, [model, comparing, baseCsg]);

  const scopeText = useMemo(() => {
    if (!csg) return '';
    const bits = [`${csg.label} · ${csg.envKind}`];
    bits.push(comparing ? `compared with ${baseCsg.label}` : 'single version, no comparison');
    if (comparing && dimUnchanged) bits.push('unchanged resources de-emphasised');
    return bits.join(' · ');
  }, [csg, comparing, baseCsg, dimUnchanged]);

  const subLine = useMemo(() => {
    if (!model) return '';
    return `${model.currentCards.length} resource group(s) from ${model.lineCount} compute line(s) · ${model.overall.instances === null ? 'instance count not stated' : `${fmt(model.overall.instances)} instance(s)`} · ${model.stamps.length} stamp(s) × ${model.tiers.length} tier(s) · ${model.attachedCount} of ${model.storageLineCount} storage line(s) attached to a resource · generated ${nowStamp()}`;
  }, [model]);

  const mapSvg = useMemo(
    () => (layout && model ? buildSvg(layout, model, { selectedKey }) : null),
    [layout, model, selectedKey]
  );

  const fit = useMemo(() => {
    if (!mapSvg) return null;
    const s = Math.min(1, PAGE_W / mapSvg.width, MAP_H / mapSvg.height);
    return {
      scale: s,
      pct: Math.max(1, Math.round(s * 100)),
      cols: Math.ceil(mapSvg.width / PAGE_W),
      rows: Math.ceil(mapSvg.height / MAP_H),
    };
  }, [mapSvg]);

  // ---- Current-state context (context only — never a mapping) ----------------
  const currentContext = useMemo(() => {
    if (!servers) return null;
    const scope = (db && db.scope) || null;
    const scoped = scopeSaved && scope ? computeScope(servers, scope) : null;
    return {
      total: servers.length,
      scoped: scoped ? scoped.inScope.length : null,
      pattern: scope ? scope.pattern : '',
      clusters: (scope && scope.clusters) || [],
      folders: (scope && scope.folders) || [],
    };
  }, [servers, db, scopeSaved]);

  // ---- Exports ---------------------------------------------------------------
  const csvRows = useMemo(() => {
    if (!model) return [];
    const out = [];
    const source = (c) => (c.status === 'Removed' && baseCsg ? baseCsg.label : model.csgLabel);
    model.cards.forEach((c) => {
      out.push({
        kind: 'Compute',
        source: source(c),
        stamp: c.stamp,
        tier: c.tier,
        resource: c.resource,
        cloudResource: c.cloudResource,
        quantity: c.qty === null ? '' : c.qty,
        altCloudResource: c.altCloudResource,
        altQuantity: c.altQty === null ? '' : c.altQty,
        osDiskType: c.osDiskType,
        osDiskGb: c.osDiskGb === null ? '' : c.osDiskGb,
        volume: '',
        diskType: '',
        capacityGb: '',
        volumeQty: '',
        iops: '',
        throughput: '',
        attachedVia: '',
        change: c.status || (comparing ? 'Unchanged' : ''),
        changeDetail: c.changeNote || '',
      });
      c.volumes.forEach((v) => out.push({
        kind: 'Storage — attached to the resource above',
        source: source(c),
        stamp: c.stamp,
        tier: c.tier,
        resource: c.resource,
        cloudResource: c.cloudResource,
        quantity: '',
        altCloudResource: '',
        altQuantity: '',
        osDiskType: '',
        osDiskGb: '',
        volume: v.desc,
        diskType: v.diskType,
        capacityGb: v.capacityGb === null ? '' : v.capacityGb,
        volumeQty: v.quantity === null ? '' : v.quantity,
        iops: v.iops === null ? '' : v.iops,
        throughput: v.throughput === null ? '' : v.throughput,
        attachedVia: `INFERRED — ${v.how}`,
        change: '',
        changeDetail: '',
      }));
    });
    model.unattached.forEach((v) => out.push({
      kind: 'Storage — NOT attached to any resource',
      source: model.csgLabel,
      stamp: v.stamp,
      tier: v.tier,
      resource: '',
      cloudResource: '',
      quantity: '',
      altCloudResource: '',
      altQuantity: '',
      osDiskType: '',
      osDiskGb: '',
      volume: v.desc,
      diskType: v.diskType,
      capacityGb: v.capacityGb === null ? '' : v.capacityGb,
      volumeQty: v.quantity === null ? '' : v.quantity,
      iops: v.iops === null ? '' : v.iops,
      throughput: v.throughput === null ? '' : v.throughput,
      attachedVia: `NOT ATTACHED — ${v.reason}`,
      change: '',
      changeDetail: '',
    }));
    return out;
  }, [model, comparing, baseCsg]);

  const doCsv = useCallback(() => {
    if (!csvRows.length) return;
    exportCsv(`target-architecture-bom-${tenantId}-${fileStamp()}.csv`, csvRows, [
      { label: 'Bill of Materials', value: 'kind' },
      { label: 'Source CSG', value: 'source' },
      { label: 'Stamp', value: 'stamp' },
      { label: 'Epic Tier', value: 'tier' },
      { label: 'Epic Resource', value: 'resource' },
      { label: 'Cloud Resource', value: 'cloudResource' },
      { label: 'Quantity', value: 'quantity' },
      { label: 'Cloud Resource (alternate)', value: 'altCloudResource' },
      { label: 'Quantity (alternate)', value: 'altQuantity' },
      { label: 'OS Disk Type', value: 'osDiskType' },
      { label: 'OS Disk Capacity (GB)', value: 'osDiskGb' },
      { label: 'Volume Description', value: 'volume' },
      { label: 'Volume Disk Type', value: 'diskType' },
      { label: 'Volume Capacity (GB)', value: 'capacityGb' },
      { label: 'Volume Quantity', value: 'volumeQty' },
      { label: 'Provisioned IOPS (blank = not supplied)', value: 'iops' },
      { label: 'Throughput (blank = not supplied)', value: 'throughput' },
      { label: 'Volume attachment (inferred)', value: 'attachedVia' },
      { label: 'Change vs comparison CSG', value: 'change' },
      { label: 'Change detail', value: 'changeDetail' },
    ]);
  }, [csvRows, tenantId]);

  const doPng = useCallback(() => {
    if (!layout || !model || !legendModel) return;
    const svg = buildSvg(layout, model, {
      header: true,
      legend: true,
      legendModel,
      meta: {
        title: `Primordia — Target Architecture · ${tenantName}`,
        scope: scopeText,
        sub: subLine,
      },
    });
    const markup = svgDoc(svg, svg.width, svg.height);
    const blob = new Blob([markup], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const px = Math.min(2, 16000 / Math.max(svg.width, svg.height, 1));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(svg.width * px);
      canvas.height = Math.round(svg.height * px);
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      exportCanvasPng(canvas, `target-architecture-${tenantId}-${fileStamp()}.png`);
      URL.revokeObjectURL(url);
    };
    img.onerror = () => URL.revokeObjectURL(url);
    img.src = url;
  }, [layout, model, legendModel, tenantId, tenantName, scopeText, subLine]);

  const doPdf = useCallback(() => {
    if (!layout) return;
    printElementToPdf(PRINT_ID, `Primordia — Target Architecture — ${tenantName}`);
  }, [layout, tenantName]);

  const onCanvasClick = useCallback((e) => {
    const g = e.target && e.target.closest ? e.target.closest('[data-card]') : null;
    if (!g) return;
    setSelectedKey(g.getAttribute('data-card') || '');
  }, []);

  const selected = useMemo(
    () => (model ? model.cards.find((c) => c.key === selectedKey) || null : null),
    [model, selectedKey]
  );

  // ---- Loading ---------------------------------------------------------------
  if (!db) {
    return (
      <Box sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={22} />
        <Typography sx={{ color: GREY }}>Reading the imported Cloud Specifications Guides…</Typography>
      </Box>
    );
  }

  // ---- Honest empty state ----------------------------------------------------
  if (!csgs.length || !csg) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD, mb: 2 }}>Target Architecture</Typography>
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <AccountTreeIcon sx={{ fontSize: 44, color: '#c7d3e0' }} />
          <Typography sx={{ fontWeight: 700, color: HEAD, mt: 1, mb: 0.5 }}>
            No Epic Cloud Specifications Guide has been imported for this workspace
          </Typography>
          <Typography variant="body2" sx={{ color: GREY, maxWidth: 700, mx: 'auto' }}>
            This diagram draws the Epic future state <b>strictly</b> from an imported CSG workbook — its Compute
            Bill of Materials gives the stamps, the Epic tiers, the cloud resources and their quantities, and its
            Storage Bill of Materials gives the volumes. <b>{tenantName}</b> has none stored, so there is nothing
            to draw and an empty grid would be misleading.
          </Typography>
          <Typography variant="body2" sx={{ color: GREY, maxWidth: 700, mx: 'auto', mt: 1.5 }}>
            Import one on the <b>Epic CSG target state</b> screen (its first tab, “Import CSG”). Come back here and
            the same workbook is drawn as a client-ready target architecture. Nothing on this page ever edits or
            re-imports a CSG — it only renders what that screen captured.
          </Typography>
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} sx={{ mt: 2 }} onClick={reload}>
            Re-check for imported CSGs
          </Button>
        </Paper>
      </Box>
    );
  }

  const zoomLabel = `${Math.round(zoom * 100)}%`;
  const prepared = clean(csg.preparedDate || csg.preparedAt || '');
  const validUntil = clean(csg.validUntil || csg.validThrough || '');

  return (
    <Box sx={{ p: 2 }}>
      {/* ---------------- Header ---------------- */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>Target Architecture</Typography>
        <Chip
          size="small"
          label={`${csg.label} · ${csg.envKind}`}
          sx={{ bgcolor: '#e8f0fe', color: NAVY, fontWeight: 700 }}
        />
        {model && (
          <Chip
            size="small"
            label={`${model.overall.instances === null ? 'instance count not stated' : `${fmt(model.overall.instances)} instances`} · ${model.stamps.length} stamp(s) × ${model.tiers.length} tier(s)`}
            sx={{ bgcolor: '#eef3f9', color: NAVY, fontWeight: 700 }}
          />
        )}
        {comparing && (
          <Chip
            size="small"
            label={`vs ${baseCsg.label}: +${model.changeCounts.Added} added · ${model.changeCounts.Removed} removed · ${model.changeCounts.Resized + model.changeCounts.Retyped} resized`}
            sx={{ bgcolor: '#f3e5f5', color: '#4a148c', fontWeight: 700 }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="contained" startIcon={<PictureAsPdfIcon />} disabled={!layout}
          onClick={doPdf} sx={{ bgcolor: NAVY, '&:hover': { bgcolor: BLUE } }}>
          PDF
        </Button>
        <Button size="small" variant="outlined" startIcon={<ImageIcon />} disabled={!layout} onClick={doPng}>PNG</Button>
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} disabled={!csvRows.length} onClick={doCsv}>
          CSV bill of materials
        </Button>
        <Tooltip title="Re-read the CSG versions stored by the CSG target-state screen">
          <span>
            <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={reload}>Reload</Button>
          </span>
        </Tooltip>
      </Stack>

      <Typography variant="body2" sx={{ color: GREY, mb: 2, maxWidth: 1040 }}>
        The Epic future state exactly as the Cloud Specifications Guide expresses it: <b>columns are stamps</b>{' '}
        (Production, Alternate Production, Build, Training, Isolated Recovery) and <b>rows are Epic tiers</b>{' '}
        (Operational Database, Relational Database, Presentation, Web &amp; Service). Each card is one Epic
        Resource with the cloud resource, quantity and disks Epic specifies for it. Storage is drawn <b>on</b> the
        resource it serves, because <b>io1 / io2 with provisioned IOPS versus gp3</b> is the distinction that moves
        an Epic build&apos;s cost and its database latency more than anything else in the workbook.
      </Typography>

      <Alert severity="warning" sx={{ mb: 2 }}>
        <AlertTitle sx={{ fontWeight: 700 }}>A CSG is a point-in-time draft, and it covers Epic only</AlertTitle>
        <Typography sx={{ fontSize: 12.5, color: GREY }}>
          This drawing is exactly as current as the import behind it — <b>{csg.label}</b>, imported{' '}
          {String(csg.importedAt).slice(0, 10)} from {csg.fileName || 'an unnamed workbook'}. Epic reissues the CSG;
          use the version comparison to see what moved. The workbook is a bill of materials for the <b>Epic
          footprint</b>, never a target state for the whole estate, and it states instance types without stating
          vCPU or RAM — so no sizing is shown here that the workbook did not write down.
        </Typography>
      </Alert>

      {model && model.unattached.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>
            {`${model.attachedCount} of ${model.storageLineCount} storage line(s) attached to a compute resource; ${model.unattached.length} could not be`}
          </AlertTitle>
          <Typography sx={{ fontSize: 12.5, color: GREY }}>
            The Storage Bill of Materials names volumes but carries no key back to the Compute Bill of Materials, so
            an attachment is <b>inferred</b> from the volume description matching an Epic Resource name (narrowed by
            tier and stamp when the storage sheet states them). Anything ambiguous is left unattached and listed —
            in its cell where the tier and stamp are known, and in the table below where they are not — rather than
            being folded into a resource it might not belong to.
          </Typography>
        </Alert>
      )}

      {model && model.overall.iops === null && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>No provisioned IOPS figure is stored for this CSG</AlertTitle>
          <Typography sx={{ fontSize: 12.5, color: GREY }}>
            The CSG import captures volume description, disk type, capacity and quantity from the Storage Bill of
            Materials; an IOPS or throughput column in the workbook is not carried into storage. Every IOPS figure
            on this page therefore reads “{DASH} not supplied”. It is not inferred from the disk type, and an io1 /
            io2 volume with no IOPS recorded is labelled as such on its card so the gap is visible rather than
            silently rounded away.
          </Typography>
        </Alert>
      )}

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-start">
        {/* ---------------- Controls + legend + detail ---------------- */}
        <Paper sx={{ p: 1.5, width: { xs: '100%', lg: 330 }, flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13, mb: 1 }}>CSG version</Typography>
          <TextField
            select fullWidth size="small" label="Draw this CSG" value={csg.id}
            onChange={(e) => { setCsgId(e.target.value); setSelectedKey(''); }} sx={{ mb: 1.25 }}
          >
            {csgs.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {`${c.label} · ${c.envKind} · imported ${String(c.importedAt).slice(0, 10)}`}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select fullWidth size="small" label="Compare with (earlier CSG)" value={baseId}
            onChange={(e) => setBaseId(e.target.value)} sx={{ mb: 1 }}
            helperText={csgs.length < 2
              ? 'Only one CSG is imported. Import the reissued workbook on the CSG screen to see what changed.'
              : 'Added, removed, re-quantified and re-typed resources are marked on the drawing.'}
          >
            <MenuItem value={NONE}>No comparison — draw this CSG alone</MenuItem>
            {csgs.filter((c) => c.id !== csg.id).map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {`${c.label} · ${c.envKind} · imported ${String(c.importedAt).slice(0, 10)}`}
              </MenuItem>
            ))}
          </TextField>

          {comparing && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Switch size="small" checked={dimUnchanged} onChange={(e) => setDimUnchanged(e.target.checked)} />
              <Typography sx={{ fontSize: 11.5, color: NAVY }}>
                De-emphasise unchanged resources (they stay drawn — the architecture is still whole)
              </Typography>
            </Stack>
          )}

          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13, mb: 0.75 }}>Legend</Typography>
          <Typography sx={{ fontSize: 10, color: GREY, fontWeight: 700, letterSpacing: 0.4, mb: 0.5 }}>
            DISK TYPE (ACCENT BAR AND VOLUME SWATCH)
          </Typography>
          <Stack spacing={0.4} sx={{ mb: 1 }}>
            {model && model.diskRows.map((d) => (
              <Stack key={d.key} direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{
                  width: 20, height: 13, borderRadius: '3px', bgcolor: d.fill, mt: '2px', flexShrink: 0,
                  border: `${d.provisioned ? 2 : 1.5}px ${d.dashed ? 'dashed' : 'solid'} ${d.color}`,
                }} />
                <Box>
                  <Typography sx={{ fontSize: 11.5, color: NAVY, fontWeight: 600 }}>{`${d.label} (${d.count})`}</Typography>
                  <Typography sx={{ fontSize: 10.5, color: GREY }}>{d.note}</Typography>
                </Box>
              </Stack>
            ))}
          </Stack>

          {comparing && (
            <>
              <Typography sx={{ fontSize: 10, color: GREY, fontWeight: 700, letterSpacing: 0.4, mb: 0.5 }}>
                {`CHANGES SINCE ${baseCsg.label.toUpperCase()}`}
              </Typography>
              <Stack spacing={0.4} sx={{ mb: 1 }}>
                {legendModel.changeRows.map((r) => (
                  <Stack key={r.status} direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{
                      width: 20, height: 13, borderRadius: '3px', bgcolor: CHANGE_STYLES[r.status].fill, mt: '2px',
                      flexShrink: 0, border: `1.5px ${r.status === 'Removed' ? 'dashed' : 'solid'} ${CHANGE_STYLES[r.status].color}`,
                    }} />
                    <Typography sx={{ fontSize: 11.5, color: NAVY }}>{`${r.label} (${r.count})`}</Typography>
                  </Stack>
                ))}
                <Typography sx={{ fontSize: 10.5, color: GREY }}>
                  Removed resources are ghosts on the drawing and are excluded from every total — the totals are
                  always {csg.label}.
                </Typography>
              </Stack>
            </>
          )}

          <Typography sx={{ fontSize: 10.5, color: GREY }}>
            “{DASH}” means the workbook did not supply that figure. <b>vCPU and RAM appear nowhere on this page</b> —
            the CSG names instance types without stating their sizing, and deriving it from a type name would be
            inventing the client&apos;s architecture.
          </Typography>

          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13, mb: 1 }}>PDF pagination</Typography>
          <TextField
            select fullWidth size="small" value={pageMode} onChange={(e) => setPageMode(e.target.value)}
            helperText={fit
              ? (pageMode === 'fit'
                ? `Scales to ${fit.pct}% on one landscape page — the scale factor is printed on the page.`
                : `Tiles at 100% across ${fit.cols * fit.rows} page(s) (${fit.cols} × ${fit.rows}), each page captioned.`)
              : 'Nothing drawn yet.'}
          >
            <MenuItem value="fit">Scale to fit one landscape page</MenuItem>
            <MenuItem value="tile">Full size, tiled across pages</MenuItem>
          </TextField>

          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch size="small" checked={showCurrent} onChange={(e) => setShowCurrent(e.target.checked)} />
            <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13 }}>Current-state context</Typography>
          </Stack>
          {showCurrent && (
            <Box sx={{ mt: 1 }}>
              {serversErr ? <Alert severity="error" sx={{ fontSize: 12 }}>{serversErr}</Alert> : null}
              {!servers && !serversErr ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={16} />
                  <Typography sx={{ fontSize: 11.5, color: GREY }}>Counting the current estate…</Typography>
                </Stack>
              ) : null}
              {currentContext && (
                <>
                  <DetailRow label="Servers today" value={`${fmt(currentContext.total)} ITComponent fact sheet(s)`} />
                  <DetailRow
                    label="Epic-scoped"
                    warn={currentContext.scoped === null}
                    value={currentContext.scoped === null
                      ? 'no Epic scope has been declared on the CSG screen, so no Epic subset can be counted'
                      : `${fmt(currentContext.scoped)} match the Epic scope declared on the CSG screen`}
                  />
                  {currentContext.scoped !== null && (
                    <Typography sx={{ fontSize: 10.5, color: GREY, mt: 0.5 }}>
                      Scope: pattern “{currentContext.pattern || 'none'}”
                      {currentContext.clusters.length ? `, ${currentContext.clusters.length} cluster(s)` : ''}
                      {currentContext.folders.length ? `, ${currentContext.folders.length} folder(s)` : ''}.
                    </Typography>
                  )}
                  <Alert severity="warning" sx={{ mt: 1, py: 0.25 }}>
                    <Typography sx={{ fontSize: 11.5 }}>
                      <b>Context only — not a mapping.</b> Nothing here says a current server becomes a particular
                      target resource. A CSG can describe a greenfield build with no current-state equivalent at
                      all. The server-by-server comparison, with its scoping caveats, lives on the CSG target-state
                      screen.
                    </Typography>
                  </Alert>
                </>
              )}
            </Box>
          )}

          {selected && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13, mb: 0.5 }}>
                {selected.resource || '(Epic Resource not named)'}
              </Typography>
              <DetailRow label="Stamp" value={selected.stamp} />
              <DetailRow label="Epic Tier" value={selected.tier} />
              <DetailRow label="Cloud resource" value={selected.cloudResource || 'not stated'} warn={!selected.cloudResource} />
              <DetailRow label="Quantity" value={selected.qty === null ? 'not stated' : fmt(selected.qty)} warn={selected.qty === null} />
              {selected.altCloudResource ? (
                <DetailRow
                  label="Alternate sizing"
                  value={`${selected.altCloudResource}${selected.altQty === null ? '' : ` × ${fmt(selected.altQty)}`}`}
                />
              ) : null}
              <DetailRow
                label="OS disk"
                warn={!selected.osDiskType || selected.osDiskGb === null}
                value={`${selected.osDiskType || 'type not stated'} · ${selected.osDiskGb === null ? 'capacity not stated' : `${fmt(selected.osDiskGb)} GB`}${selected.osDiskMixed ? ' (folded lines disagree)' : ''}`}
              />
              <DetailRow label="vCPU / RAM" value="not stated by the CSG — never inferred" warn />
              <DetailRow
                label="Volumes"
                warn={!selected.volumes.length}
                value={selected.volumes.length
                  ? `${selected.volumes.length} attached (inferred by name)`
                  : 'none matched from the Storage Bill of Materials'}
              />
              {selected.volumes.map((v) => (
                <Typography key={v.id} sx={{ fontSize: 11, color: GREY, pl: 1 }}>
                  • {v.desc} — {volumeSpec(v)}
                </Typography>
              ))}
              {selected.changeNote ? <DetailRow label="Change" value={selected.changeNote} /> : null}
              <Typography sx={{ fontSize: 10.5, color: GREY, mt: 0.75 }}>
                Folded from {selected.lineCount} Compute Bill of Materials line(s).
              </Typography>
            </>
          )}
        </Paper>

        {/* ---------------- Canvas ---------------- */}
        <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
          {!layout && (
            <Alert severity="warning">
              <AlertTitle sx={{ fontWeight: 700 }}>This CSG has no compute line items</AlertTitle>
              {`"${csg.label}" stored ${model ? model.lineCount : 0} compute line(s), so there is no target architecture to draw. Re-import the workbook on the CSG screen and check the Compute Bill of Materials column mapping.`}
            </Alert>
          )}

          {layout && mapSvg && (
            <Paper sx={{ p: 0, position: 'relative' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13 }}>
                  {`${csg.label} — Epic target architecture`}
                </Typography>
                <Chip
                  size="small"
                  label={`${model.currentCards.length} resource group(s) · ${model.lineCount} compute line(s)`}
                  sx={{ height: 20, fontSize: 11, bgcolor: '#eef3f9', color: NAVY, fontWeight: 700 }}
                />
                <Box sx={{ flex: 1 }} />
                <Button size="small" startIcon={<ZoomOutIcon />} onClick={() => setZoom((z) => Math.max(0.25, Math.round((z - 0.15) * 100) / 100))}>
                  Out
                </Button>
                <Typography sx={{ fontSize: 12, color: GREY, minWidth: 44, textAlign: 'center' }}>{zoomLabel}</Typography>
                <Button size="small" startIcon={<ZoomInIcon />} onClick={() => setZoom((z) => Math.min(2, Math.round((z + 0.15) * 100) / 100))}>
                  In
                </Button>
              </Stack>
              <Divider />
              <Box
                onClick={onCanvasClick}
                sx={{ overflow: 'auto', maxHeight: 780, bgcolor: '#fff', p: 1 }}
                dangerouslySetInnerHTML={{ __html: svgDoc(mapSvg, mapSvg.width * zoom, mapSvg.height * zoom) }}
              />
              <Divider />
              <Typography sx={{ fontSize: 11.5, color: GREY, p: 1.25 }}>
                Click a resource for its full record; hover for the same detail as a tooltip. What you see here is
                exactly what the PDF and PNG contain — one drawing routine feeds all three.
                {fit && pageMode === 'fit' && ` The PDF prints at ${fit.pct}% of full size on one landscape page.`}
                {fit && pageMode === 'tile' && ` The PDF prints full size across ${fit.cols * fit.rows} landscape page(s).`}
              </Typography>
            </Paper>
          )}
        </Box>
      </Stack>

      {/* ---------------- Hidden print block (the PDF payload) ---------------- */}
      <div id={PRINT_ID} style={{ display: 'none' }}>
        <style>{PRINT_CSS}</style>
        {layout && mapSvg && fit && model && (
          <>
            <div className="ta-title">
              <PrimordiaLogo size={30} />
              <div>
                <h1>Primordia — Target Architecture</h1>
                <div className="sub">{tenantName} — Epic future state from the Cloud Specifications Guide</div>
              </div>
            </div>
            <div className="ta-meta">
              <span><b>CSG:</b> {csg.label}</span>
              <span><b>Environment:</b> {csg.envKind}</span>
              <span><b>Prepared:</b> {prepared || 'not captured by the CSG import'}</span>
              <span><b>Valid until:</b> {validUntil || 'not captured by the CSG import'}</span>
              <span><b>Workbook:</b> {csg.fileName || 'file name not recorded'}</span>
              <span><b>Imported:</b> {String(csg.importedAt).slice(0, 16).replace('T', ' ')}</span>
              <span><b>Comparison:</b> {comparing ? baseCsg.label : 'none — single version'}</span>
              <span><b>Workspace:</b> {tenantId}</span>
              <span><b>Generated:</b> {nowStamp()}</span>
            </div>
            <div className="ta-meta">
              <span><b>Instances:</b> {model.overall.instances === null ? `${DASH} not supplied` : fmt(model.overall.instances)}</span>
              <span><b>Resource groups:</b> {model.currentCards.length} from {model.lineCount} compute line(s)</span>
              <span><b>Distinct cloud resource types:</b> {fmt(model.overall.distinctResources)}</span>
              <span><b>Stamps × tiers:</b> {model.stamps.length} × {model.tiers.length}</span>
              <span><b>OS disk capacity:</b> {model.overall.osDiskGb === null ? `${DASH} not supplied` : `${fmt(model.overall.osDiskGb)} GB`}</span>
              <span><b>Storage BoM capacity:</b> {model.overall.storageGb === null ? `${DASH} not supplied` : `${fmt(model.overall.storageGb)} GB`}</span>
              <span><b>Provisioned IOPS:</b> {model.overall.iops === null ? `${DASH} not supplied` : fmt(model.overall.iops)}</span>
              <span><b>vCPU / RAM:</b> {DASH} not stated by the CSG</span>
            </div>
            <div className="ta-note">
              The Cloud Specifications Guide is a <b>point-in-time draft for the Epic footprint only</b> and Epic
              reissues it; this drawing is exactly as current as the import named above, and it is not a target
              state for the rest of the estate. The workbook states cloud resource types and quantities but not
              vCPU or RAM, so no sizing appears here that the workbook did not write down — anything it did not
              supply prints as “{DASH}”. Storage volumes are attached to resources by <b>inferred</b> name match
              ({model.attachedCount} of {model.storageLineCount} attached; {model.unattached.length} left
              unattached and listed).{' '}
              {pageMode === 'fit'
                ? `The diagram follows on its own landscape page, scaled to ${fit.pct}% of full size (nothing is cropped).`
                : `The diagram follows at 100%, tiled across ${fit.cols * fit.rows} landscape page(s) in a ${fit.cols} × ${fit.rows} grid — read left to right, top to bottom. Nothing is cropped.`}
            </div>

            <div className="ta-legend">
              <h2>Legend</h2>
              <h3>LAYOUT</h3>
              <div>
                Columns are Epic <b>stamps</b>; rows are Epic <b>tiers</b>. That is the structure the CSG itself
                uses, preserved rather than redrawn. Each card is one Epic Resource: its cloud resource (AWS
                instance type) × quantity, its OS disk, and the Storage Bill of Materials volumes matched to it.
              </div>
              <h3>DISK TYPE (ACCENT BAR AND VOLUME SWATCH)</h3>
              <div className="items">
                {model.diskRows.map((d) => (
                  <span className="it" key={d.key}>
                    <span
                      className="sw"
                      style={{ background: d.fill, border: `${d.provisioned ? 2 : 1.5}px ${d.dashed ? 'dashed' : 'solid'} ${d.color}` }}
                    />
                    <span>{d.label} ({d.count})</span>
                  </span>
                ))}
              </div>
              <div style={{ marginTop: 4 }}>
                io1 / io2 buy provisioned IOPS and gp3 does not — the single most cost- and
                performance-significant choice in an Epic build, so it is the loudest mark on the page.
              </div>
              {comparing && (
                <>
                  <h3>CHANGES SINCE {baseCsg.label.toUpperCase()}</h3>
                  <div className="items">
                    {legendModel.changeRows.map((r) => (
                      <span className="it" key={r.status}>
                        <span
                          className="sw"
                          style={{ background: CHANGE_STYLES[r.status].fill, border: `1.5px ${r.status === 'Removed' ? 'dashed' : 'solid'} ${CHANGE_STYLES[r.status].color}` }}
                        />
                        <span>{r.label} ({r.count})</span>
                      </span>
                    ))}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    Removed resources are drawn as dashed ghosts so the change is visible; they are excluded from
                    every total on this page. All totals are {csg.label}.
                  </div>
                </>
              )}
              <h3>HONESTY</h3>
              <div>
                “{DASH}” means the workbook did not supply that figure — it has not been estimated. vCPU and RAM
                appear nowhere: the CSG names instance types without stating their sizing.
              </div>
              <div>
                Volume attachments are inferred from the volume description matching an Epic Resource name; the
                workbook carries no key between the two bills of materials. Every attachment and every failure to
                attach is listed in the bill of materials table and the CSV.
              </div>
              <div style={{ marginTop: 4, fontWeight: 700, color: NAVY }}>
                {model.currentCards.length} resource group(s) · {model.overall.instances === null ? `${DASH} instances` : `${fmt(model.overall.instances)} instance(s)`} · {model.stamps.length} stamp(s) × {model.tiers.length} tier(s).
              </div>
            </div>

            {pageMode === 'fit' ? (
              <div className="ta-page ta-mappage">
                <div
                  dangerouslySetInnerHTML={{
                    __html: svgDoc(mapSvg, mapSvg.width * fit.scale, mapSvg.height * fit.scale),
                  }}
                />
                <div className="ta-cap">
                  {`${tenantName} — ${csg.label} (${csg.envKind}) — Epic target architecture — whole diagram on one page at ${fit.pct}% of full size, nothing cropped.`}
                </div>
              </div>
            ) : (
              Array.from({ length: fit.cols * fit.rows }).map((unused, i) => {
                const c = i % fit.cols;
                const r = Math.floor(i / fit.cols);
                return (
                  <div className="ta-page ta-mappage" key={`tile-${r}-${c}`}>
                    <div
                      className="ta-tile"
                      style={{ width: PAGE_W, height: MAP_H }}
                      dangerouslySetInnerHTML={{
                        __html: `<div style="left:${-c * PAGE_W}px;top:${-r * MAP_H}px">${svgDoc(mapSvg, mapSvg.width, mapSvg.height)}</div>`,
                      }}
                    />
                    <div className="ta-cap">
                      {`Diagram tile row ${r + 1} of ${fit.rows}, column ${c + 1} of ${fit.cols} — ${tenantName} — ${csg.label} — 100% scale`}
                    </div>
                  </div>
                );
              })
            )}

            <h2 className="ta-h">Totals per stamp</h2>
            <table className="ta-tbl">
              <thead>
                <tr>
                  <th>Stamp</th><th>Resource groups</th><th>Instances</th><th>OS disk capacity</th>
                  <th>Attached volume capacity</th><th>Provisioned IOPS</th>
                </tr>
              </thead>
              <tbody>
                {model.stamps.map((s) => {
                  const t = model.stampTotals.get(s) || {};
                  return (
                    <tr key={`t-${s}`}>
                      <td><b>{s}</b></td>
                      <td>{fmt(t.cardCount)}</td>
                      <td>{t.instances === null ? `${DASH} not supplied` : fmt(t.instances)}</td>
                      <td>{t.osDiskGb === null ? `${DASH} not supplied` : `${fmt(t.osDiskGb)} GB`}</td>
                      <td>{t.volumeGb === null ? `${DASH} not supplied` : `${fmt(t.volumeGb)} GB`}</td>
                      <td>{t.iops === null ? `${DASH} not supplied` : fmt(t.iops)}</td>
                    </tr>
                  );
                })}
                <tr>
                  <td><b>All stamps</b></td>
                  <td><b>{fmt(model.currentCards.length)}</b></td>
                  <td><b>{model.overall.instances === null ? `${DASH} not supplied` : fmt(model.overall.instances)}</b></td>
                  <td><b>{model.overall.osDiskGb === null ? `${DASH} not supplied` : `${fmt(model.overall.osDiskGb)} GB`}</b></td>
                  <td><b>{model.overall.storageGb === null ? `${DASH} not supplied` : `${fmt(model.overall.storageGb)} GB (all storage lines, attached or not)`}</b></td>
                  <td><b>{model.overall.iops === null ? `${DASH} not supplied` : fmt(model.overall.iops)}</b></td>
                </tr>
              </tbody>
            </table>

            <h2 className="ta-h">{`Bill of materials — ${csvRows.length} row(s)`}</h2>
            <table className="ta-tbl">
              <thead>
                <tr>
                  <th>Line</th><th>Stamp</th><th>Epic Tier</th><th>Epic Resource</th><th>Cloud resource</th>
                  <th>Qty</th><th>OS disk</th><th>Volume</th><th>Volume spec</th><th>Change</th>
                </tr>
              </thead>
              <tbody>
                {csvRows.map((r, i) => (
                  <tr key={`bom-${i}-${r.kind}-${r.resource}-${r.volume}`}>
                    <td>{r.kind}</td>
                    <td>{r.stamp || DASH}</td>
                    <td>{r.tier || DASH}</td>
                    <td>{r.resource || DASH}</td>
                    <td>{r.cloudResource || DASH}</td>
                    <td>{r.quantity === '' ? DASH : r.quantity}</td>
                    <td>{r.osDiskType ? `${r.osDiskType} · ${r.osDiskGb === '' ? DASH : `${r.osDiskGb} GB`}` : DASH}</td>
                    <td>{r.volume || DASH}</td>
                    <td>
                      {r.volume
                        ? `${r.diskType || 'disk type not stated'} · ${r.capacityGb === '' ? 'capacity not supplied' : `${r.capacityGb} GB`} · ${r.iops === '' ? 'IOPS not supplied' : `${r.iops} IOPS`} — ${r.attachedVia}`
                        : DASH}
                    </td>
                    <td>{r.change ? `${r.change}${r.changeDetail ? ` — ${r.changeDetail}` : ''}` : DASH}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* ---------------- Unattached storage, on screen ---------------- */}
      {model && model.orphans.length > 0 && (
        <Paper sx={{ p: 1.5, mt: 2, overflowX: 'auto' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 14 }}>
              Storage volumes not attached to any resource
            </Typography>
            <Chip
              size="small"
              label={`${model.orphans.length} of ${model.storageLineCount}`}
              sx={{ height: 20, fontSize: 11, bgcolor: '#fff3e0', color: '#8a5a00', fontWeight: 700 }}
            />
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: 11.5, color: GREY }}>
              Listed, never absorbed into a resource they might not belong to.
            </Typography>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: HEAD, fontSize: 12, whiteSpace: 'nowrap' } }}>
                <TableCell>Volume description</TableCell>
                <TableCell>Disk type</TableCell>
                <TableCell align="right">Capacity (GB)</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">IOPS</TableCell>
                <TableCell>Why it is not attached</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {model.orphans.map((v) => (
                <TableRow key={v.id} hover>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600, color: NAVY }}>{v.desc}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    <Chip
                      size="small"
                      label={v.diskType || 'not stated'}
                      sx={{
                        height: 20, fontSize: 11, fontWeight: 600, bgcolor: diskStyle(v.diskType).fill, color: NAVY,
                        border: `1.5px ${diskStyle(v.diskType).dashed ? 'dashed' : 'solid'} ${diskStyle(v.diskType).color}`,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>{v.capacityGb === null ? DASH : fmt(v.capacityGb)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>{v.quantity === null ? DASH : fmt(v.quantity)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, color: v.iops === null ? GREY : NAVY }}>
                    {v.iops === null ? DASH : fmt(v.iops)}
                  </TableCell>
                  <TableCell sx={{ fontSize: 11.5, color: '#8a5a00' }}>{v.reason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* ---------------- On-screen bill of materials ---------------- */}
      {model && model.cards.length > 0 && (
        <Paper sx={{ p: 1.5, mt: 2, overflowX: 'auto' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 14 }}>Compute bill of materials</Typography>
            <Chip
              size="small"
              label={`${model.currentCards.length} resource group(s) · ${model.lineCount} line(s)`}
              sx={{ height: 20, fontSize: 11, bgcolor: '#eef3f9', color: NAVY, fontWeight: 700 }}
            />
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: 11.5, color: GREY }}>
              Every row is one card on the drawing. Quantities are the workbook&apos;s; blanks are the workbook&apos;s too.
            </Typography>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: HEAD, fontSize: 12, whiteSpace: 'nowrap' } }}>
                <TableCell>Stamp</TableCell>
                <TableCell>Epic Tier</TableCell>
                <TableCell>Epic Resource</TableCell>
                <TableCell>Cloud resource</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell>OS disk</TableCell>
                <TableCell>Attached volumes (inferred)</TableCell>
                {comparing ? <TableCell>Change</TableCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {model.cards.map((c) => (
                <TableRow
                  key={c.key}
                  hover
                  selected={c.key === selectedKey}
                  onClick={() => setSelectedKey(c.key)}
                  sx={{ cursor: 'pointer', opacity: c.status === 'Removed' ? 0.7 : 1 }}
                >
                  <TableCell sx={{ fontSize: 12, color: GREY }}>{c.stamp}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: GREY }}>{c.tier}</TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600, color: NAVY }}>{c.resource || DASH}</TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 700, color: c.cloudResource ? BLUE : GREY }}>
                    {c.cloudResource || 'not stated'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, color: c.qty === null ? GREY : NAVY }}>
                    {c.qty === null ? DASH : fmt(c.qty)}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: c.osDiskType ? GREY : '#8a5a00' }}>
                    {`${c.osDiskType || 'type not stated'} · ${c.osDiskGb === null ? 'capacity not stated' : `${fmt(c.osDiskGb)} GB`}`}
                  </TableCell>
                  <TableCell sx={{ fontSize: 11.5, color: GREY, maxWidth: 340 }}>
                    {c.volumes.length ? (
                      <Stack spacing={0.25}>
                        {c.volumes.map((v) => (
                          <Tooltip key={v.id} title={`Inferred attachment — ${v.how}`}>
                            <span style={{ color: diskStyle(v.diskType).provisioned ? '#9d2020' : GREY }}>
                              {`${v.desc} — ${volumeSpec(v)}`}
                            </span>
                          </Tooltip>
                        ))}
                      </Stack>
                    ) : <span style={{ color: '#8a5a00' }}>none matched</span>}
                  </TableCell>
                  {comparing ? (
                    <TableCell sx={{ fontSize: 11.5 }}>
                      <Chip
                        size="small"
                        label={c.status || 'Unchanged'}
                        sx={{
                          height: 20, fontSize: 11, fontWeight: 700,
                          bgcolor: (CHANGE_STYLES[c.status] || {}).fill || '#f0f2f5',
                          color: (CHANGE_STYLES[c.status] || {}).color || GREY,
                        }}
                      />
                      {c.changeNote ? (
                        <Typography sx={{ fontSize: 11, color: GREY }}>{c.changeNote}</Typography>
                      ) : null}
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}

function DetailRow({ label, value, warn }) {
  return (
    <Stack direction="row" spacing={1} sx={{ py: 0.25 }} alignItems="flex-start">
      <Typography sx={{ fontSize: 11, color: GREY, width: 104, flexShrink: 0 }}>{label}</Typography>
      <Typography sx={{ fontSize: 11.5, color: warn ? '#8a5a00' : NAVY, fontWeight: warn ? 700 : 500, wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Stack>
  );
}
